import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Badge, Button } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import { useCanManageContracts } from '@/hooks/useCanManageContracts'
import {
  createContractRenewalFromContract,
  listDocumentsRegistry,
} from '@/services/documentsRegistryService'
import { loadAllPartnerLegalEntities } from '@/services/royaltyService'
import { AddPartnerEntityInline } from '@/components/contract/AddPartnerEntityInline'
import {
  createContractForSeries,
  fetchContractPendingSeries,
  fetchStudioPurchaseCandidates,
  fetchStudioPurchasePartners,
  planStudioPurchase,
  type ContractPendingSeries,
  type StudioPurchaseCandidate,
  type StudioPurchasePartner,
} from '@/services/studioPurchaseService'
import type {
  StudioDocumentsRegistryRow,
  PartnerLegalEntity,
} from '@/types'
import {
  CONTRACT_STATUS_LABELS,
  EXPIRY_BADGE,
  contractStatusTone,
  deriveContractStatus,
  expirySortKey,
  fmtDate,
  formatContractDurationYears,
  formatFranchiseeLabel,
  getContractDates,
  getExpiryInfo,
  isActiveOrRecognized,
  matchesExpiryFilter,
  type ExpiryFilter,
} from '@/lib/documentsRegistry'
import { DocumentsRegistryShowcase } from '@/pages/DocumentsRegistryShowcasePage'
import { FileText, Search, AlertTriangle, Plus, RefreshCw, X } from 'lucide-react'

const registryErrorAlertCls =
  'flex items-start gap-2 rounded-md border-2 border-destructive bg-destructive/15 px-3 py-2.5 text-sm font-medium text-destructive shadow-sm'

const DOCUMENTS_REGISTRY_FILTERS_KEY = 'documents-registry:filters'

interface DocumentsRegistrySavedFilters {
  search: string
  showActiveOnly: boolean
  showDraftsOnly: boolean
  showExtendedOnly: boolean
  expiryFilter: ExpiryFilter
  sortByExpiry: boolean
}

const EXPIRY_FILTERS = new Set<ExpiryFilter>([
  'ALL', 'EXPIRING', 'OVERDUE', 'LT_1M', 'LT_3M', 'LT_6M', 'NO_DATE',
])

function readDocumentsRegistryFilters(): Partial<DocumentsRegistrySavedFilters> {
  try {
    const raw = sessionStorage.getItem(DOCUMENTS_REGISTRY_FILTERS_KEY)
    if (!raw) return {}
    const saved = JSON.parse(raw) as Partial<DocumentsRegistrySavedFilters>
    return {
      search: typeof saved.search === 'string' ? saved.search : undefined,
      showActiveOnly: typeof saved.showActiveOnly === 'boolean' ? saved.showActiveOnly : undefined,
      showDraftsOnly: typeof saved.showDraftsOnly === 'boolean' ? saved.showDraftsOnly : undefined,
      showExtendedOnly: typeof saved.showExtendedOnly === 'boolean' ? saved.showExtendedOnly : undefined,
      expiryFilter: saved.expiryFilter && EXPIRY_FILTERS.has(saved.expiryFilter)
        ? saved.expiryFilter
        : undefined,
      sortByExpiry: typeof saved.sortByExpiry === 'boolean' ? saved.sortByExpiry : undefined,
    }
  } catch {
    return {}
  }
}

function RegistryErrorAlert({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div role="alert" aria-live="assertive" className={`${registryErrorAlertCls} ${className}`}>
      <AlertTriangle size={16} className="mt-0.5 flex-none" />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  )
}

const OWNERSHIP_PHASE_LABELS: Record<string, string> = {
  BUYING: 'зашёл на покупку',
  PLANNED: 'планируется к открытию',
  NOT_OPENED: 'не открылась',
  OWNED: 'владеет',
  FOR_SALE: 'на продаже',
}

function toDateInputValue(value: string | null): string {
  if (!value) return ''
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : ''
}

function dateInputDisplay(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? fmtDate(value) : value
}

function parseDateInputDisplay(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) return trimmed
  const local = trimmed.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/)
  if (!local) return trimmed
  return `${local[3]}-${local[2]}-${local[1]}`
}

function isValidIsoDate(value: string): boolean {
  if (!value) return true
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  const d = new Date(year, month - 1, day)
  return (
    d.getFullYear() === year &&
    d.getMonth() === month - 1 &&
    d.getDate() === day
  )
}

function addDaysIso(value: string | null, days: number): string {
  const base = toDateInputValue(value)
  if (!base) return ''
  const d = new Date(`${base}T00:00:00`)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

/**
 * Развилка реестра документов по уровню доступа УК.
 *
 * Расширенный экран (грид со всеми ДКК, «Новый ДКК», переход в карточку договора)
 * остаётся только у advanced. Все остальные — standard-сотрудники УК и роли без
 * доступа к УК вовсе — попадают на витрину: действующие и признаваемые
 * действующими договоры, без действий и без провала в карточку.
 *
 * Уровень доступа разрешается ДО рендера: иначе advanced на мгновение увидел бы
 * витрину и её выборку (`getAccessLevel` асинхронный, кэш в localStorage без TTL).
 */
export function DocumentsRegistryPage() {
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const [accessLevel, setAccessLevel] = useState<'standard' | 'advanced' | null | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    if (!user?.id) {
      setAccessLevel(null)
      return
    }
    setAccessLevel(undefined)
    getAccessLevel().then(level => {
      if (!cancelled) setAccessLevel(level)
    })
    return () => {
      cancelled = true
    }
  }, [getAccessLevel, user?.id])

  if (accessLevel === undefined) {
    return <div className="text-sm text-muted-foreground">Загрузка…</div>
  }

  if (accessLevel !== 'advanced') {
    return <DocumentsRegistryShowcase />
  }

  return <AdvancedDocumentsRegistry />
}

function AdvancedDocumentsRegistry() {
  const navigate = useNavigate()
  const [savedFilters] = useState(readDocumentsRegistryFilters)
  const [rows, setRows] = useState<StudioDocumentsRegistryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState(savedFilters.search ?? '')
  const [showActiveOnly, setShowActiveOnly] = useState(savedFilters.showActiveOnly ?? false)
  const [showDraftsOnly, setShowDraftsOnly] = useState(savedFilters.showDraftsOnly ?? false)
  const [showExtendedOnly, setShowExtendedOnly] = useState(savedFilters.showExtendedOnly ?? false)
  const [expiryFilter, setExpiryFilter] = useState<ExpiryFilter>(savedFilters.expiryFilter ?? 'ALL')
  const [sortByExpiry, setSortByExpiry] = useState(savedFilters.sortByExpiry ?? false)
  const [reloadKey, setReloadKey] = useState(0)
  const [showNewContract, setShowNewContract] = useState(false)
  // Заводить ДКК с 01.09.2026 могут только юристы и супер-админы — зеркало
  // серверного гейта (contract_require_lawyer), чтобы кнопка не падала в лицо.
  const { canManage: canManageContracts } = useCanManageContracts()

  useEffect(() => {
    sessionStorage.setItem(
      DOCUMENTS_REGISTRY_FILTERS_KEY,
      JSON.stringify({
        search,
        showActiveOnly,
        showDraftsOnly,
        showExtendedOnly,
        expiryFilter,
        sortByExpiry,
      } satisfies DocumentsRegistrySavedFilters),
    )
  }, [search, showActiveOnly, showDraftsOnly, showExtendedOnly, expiryFilter, sortByExpiry])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const handle = setTimeout(() => {
      // Без limit сервис постранично тянет весь реестр: PostgREST max-rows = 1000
      // на PROD режет любой одиночный ответ, а ДКК уже больше 1000.
      listDocumentsRegistry({ search })
        .then(data => {
          if (!cancelled) {
            setRows(data)
            setError(null)
          }
        })
        .catch(e => {
          if (!cancelled) setError(e instanceof Error ? e.message : String(e))
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [search, reloadKey])

  const summary = useMemo(() => {
    const total = rows.length
    const active = rows.filter(r => deriveContractStatus(r) === 'ACTIVE_SIGNED').length
    let overdue = 0
    let expiringWithinMonth = 0
    let expiringWithinThreeMonths = 0
    for (const r of rows) {
      const cat = getExpiryInfo(r).category
      if (cat === 'OVERDUE') overdue += 1
      else if (cat === 'LT_1M') {
        expiringWithinMonth += 1
        expiringWithinThreeMonths += 1
      }
      else if (cat === 'LT_3M') expiringWithinThreeMonths += 1
    }
    return { total, active, overdue, expiringWithinMonth, expiringWithinThreeMonths }
  }, [rows])

  const visibleRows = useMemo(() => {
    let out = showActiveOnly ? rows.filter(isActiveOrRecognized) : rows
    if (showDraftsOnly) out = out.filter(r => r.contract_activated_at == null)
    if (showExtendedOnly) out = out.filter(r => r.has_term_extension_amendment === true)
    if (expiryFilter !== 'ALL') out = out.filter(r => matchesExpiryFilter(r, expiryFilter))
    if (sortByExpiry) out = [...out].sort((a, b) => expirySortKey(a) - expirySortKey(b))
    return out
  }, [rows, showActiveOnly, showDraftsOnly, showExtendedOnly, expiryFilter, sortByExpiry])

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-primary" />
          <h1 className="text-lg font-semibold">Реестр документов</h1>
        </div>
        <div className="flex items-center gap-2">
          {canManageContracts && (
            <Button size="sm" onClick={() => setShowNewContract(true)}>
              <Plus size={14} className="mr-1" /> Новый ДКК
            </Button>
          )}
          <div className="text-xs text-muted-foreground text-right">
            <div>
              ДКК {summary.total} · Действует {summary.active}
              {' · '}<span className="text-error">Просрочено {summary.overdue}</span>
            </div>
            <div className="mt-0.5">
              Истекает: ≤1 мес {summary.expiringWithinMonth}
              {' · '}≤3 мес {summary.expiringWithinThreeMonths}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Франчайзи, студия, город, № ДКК, ИНН…"
            className="w-full pl-9 pr-3 py-2 border border-border rounded-md bg-background text-sm"
          />
        </div>
        <select
          value={expiryFilter}
          onChange={e => setExpiryFilter(e.target.value as ExpiryFilter)}
          className="rounded-md border border-border px-3 py-2 text-sm bg-background"
          title="Фильтр по сроку окончания"
        >
          <option value="ALL">Все сроки</option>
          <option value="EXPIRING">Истекающие (≤6 мес)</option>
          <option value="OVERDUE">Просрочен</option>
          <option value="LT_1M">До 1 мес</option>
          <option value="LT_3M">До 3 мес</option>
          <option value="LT_6M">До 6 мес</option>
          <option value="NO_DATE">Без даты окончания</option>
        </select>
        <label className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer select-none bg-background">
          <input
            type="checkbox"
            checked={showExtendedOnly}
            onChange={e => setShowExtendedOnly(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          <span>Было продление</span>
        </label>
        <label className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer select-none bg-background">
          <input
            type="checkbox"
            checked={sortByExpiry}
            onChange={e => setSortByExpiry(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          <span>По сроку окончания</span>
        </label>
        <label className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer select-none bg-background">
          <input
            type="checkbox"
            checked={showDraftsOnly}
            onChange={e => setShowDraftsOnly(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          <span>Ждут подписи</span>
        </label>
        <label className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer select-none bg-background">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={e => setShowActiveOnly(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          <span>Действующие</span>
        </label>
      </div>

      {error && (
        <RegistryErrorAlert>{error}</RegistryErrorAlert>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Загрузка…</div>
      ) : visibleRows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Ничего не найдено.</div>
      ) : (
        <Card className="py-2 overflow-x-auto">
          <div className="min-w-[980px]">
            <div className="grid grid-cols-[64px_minmax(210px,1.7fr)_minmax(100px,0.85fr)_minmax(130px,1fr)_minmax(160px,1fr)_minmax(180px,1.15fr)_90px] gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border">
              <div>Студия</div>
              <div>Франчайзи</div>
              <div>Город</div>
              <div>№ ДКК</div>
              <div>Статус договора</div>
              <div>Срок ДКК</div>
              <div>Продление по ДС</div>
            </div>
            {visibleRows.map(row => (
              (() => {
                const status = deriveContractStatus(row)
                const dates = getContractDates(row)
                const expiry = getExpiryInfo(row)
                const duration = formatContractDurationYears(
                  dates.effectiveFrom,
                  dates.actualEnd ?? dates.expectedEnd,
                )
                return (
              <div
                key={row.contract_id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/management/documents-registry/${row.contract_id}`)}
                onKeyDown={e => {
                  if (e.key === 'Enter') navigate(`/management/documents-registry/${row.contract_id}`)
                }}
                className="w-full text-left grid grid-cols-[64px_minmax(210px,1.7fr)_minmax(100px,0.85fr)_minmax(130px,1fr)_minmax(160px,1fr)_minmax(180px,1.15fr)_90px] gap-2 px-3 py-2 border-b border-border last:border-0 hover:bg-muted/30 items-center cursor-pointer"
              >
                <div className="text-sm font-semibold">{row.studio_number ?? '—'}</div>
                <div className="min-w-0">
                  <div className="text-sm truncate">{formatFranchiseeLabel(row)}</div>
                  <div className="text-xs text-muted-foreground truncate">{row.partner_legal_name ?? ''}</div>
                </div>
                <div className="text-sm truncate">{row.city_name ?? '—'}</div>
                <div className="text-sm truncate">{row.contract_number ?? '—'}</div>
                <div>
                  <Badge variant={contractStatusTone(status)}>
                    {row.legal_status_label ?? CONTRACT_STATUS_LABELS[status]}
                  </Badge>
                  {row.contract_activated_at == null && (
                    <Badge variant="warning" className="mt-1 block w-fit px-2 py-0.5 text-xs">
                      Ждёт подписи франчайзи
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  <div>
                    {fmtDate(dates.effectiveFrom)} — {fmtDate(dates.actualEnd ?? dates.expectedEnd)}
                    {duration && ` · ${duration}`}
                  </div>
                  {expiry.category !== 'NONE' && expiry.category !== 'OK' && (
                    <Badge variant={EXPIRY_BADGE[expiry.category].tone} className="mt-1 px-2 py-0.5 text-xs">
                      {EXPIRY_BADGE[expiry.category].label}
                      {expiry.daysLeft != null && expiry.category !== 'NO_DATE'
                        ? ` · ${expiry.category === 'OVERDUE' ? Math.abs(expiry.daysLeft) : expiry.daysLeft} дн.`
                        : ''}
                    </Badge>
                  )}
                </div>
                <div className="text-sm">{row.has_term_extension_amendment ? 'Да' : 'Нет'}</div>
              </div>
                )
              })()
            ))}
          </div>
        </Card>
      )}

      {showNewContract && (
        <NewContractDialog
          existingContracts={rows}
          onClose={() => setShowNewContract(false)}
          onCreated={newContractId => {
            setShowNewContract(false)
            setReloadKey(k => k + 1)
            navigate(`/management/documents-registry/${newContractId}`)
          }}
        />
      )}
    </div>
  )
}

function contractOptionLabel(row: StudioDocumentsRegistryRow): string {
  return `Студия ${row.studio_number ?? '—'} · ${row.contract_number ?? `#${row.contract_id}`} · ${row.partner_fio ?? `Франчайзи ${row.partner_number ?? '—'}`}`
}

function contractSearchText(row: StudioDocumentsRegistryRow): string {
  return [
    row.studio_number,
    row.contract_number,
    row.partner_fio,
    row.partner_number,
    row.partner_legal_name,
    row.city_name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function studioOptionLabel(row: StudioPurchaseCandidate): string {
  const studio = row.studio_number ?? row.studio_internal_id
  return `№${studio} · ${row.city_name ?? 'город не указан'}${row.address ? ` · ${row.address}` : ''}`
}

function partnerOptionLabel(row: StudioPurchasePartner): string {
  return `№${row.partner_number} · ${row.persons_fio || 'без ФИО'}`
}

function optionMatches(values: Array<string | number | null | undefined>, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return values
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLowerCase()
    .includes(needle)
}

// Реестр — больше 1300 ДКК, из них подавляющее большинство уже закрыты,
// поэтому список продления без поиска нечитаем: фильтруем по студии/номеру/
// партнёру/городу и поднимаем действующие договоры наверх.
const CONTRACT_OPTIONS_LIMIT = 80

function ContractSearchSelect({
  contracts,
  value,
  onChange,
}: {
  contracts: StudioDocumentsRegistryRow[]
  value: string
  onChange: (contractId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selected = useMemo(
    () => contracts.find(row => String(row.contract_id) === value) ?? null,
    [contracts, value]
  )

  const matched = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const rows = needle
      ? contracts.filter(row => contractSearchText(row).includes(needle))
      : contracts
    return [...rows].sort(
      (a, b) => Number(!isActiveOrRecognized(a)) - Number(!isActiveOrRecognized(b))
    )
  }, [contracts, query])

  const options = matched.slice(0, CONTRACT_OPTIONS_LIMIT)

  return (
    <div
      className="relative"
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm text-left"
      >
        {selected ? contractOptionLabel(selected) : <span className="text-muted-foreground">— выберите договор —</span>}
      </button>

      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
          <div className="relative border-b border-border p-2">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Студия, № ДКК, франчайзи, город…"
              className="w-full pl-7 pr-2 py-1.5 border border-border rounded bg-background text-sm"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">Ничего не найдено.</div>
            ) : (
              options.map(row => {
                const status = deriveContractStatus(row)
                return (
                  <button
                    key={row.contract_id}
                    type="button"
                    onClick={() => {
                      onChange(String(row.contract_id))
                      setQuery('')
                      setOpen(false)
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-muted/40 ${
                      String(row.contract_id) === value ? 'bg-muted/30' : ''
                    }`}
                  >
                    <div className="truncate">{contractOptionLabel(row)}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {row.city_name ?? '—'} · {row.legal_status_label ?? CONTRACT_STATUS_LABELS[status]}
                    </div>
                  </button>
                )
              })
            )}
          </div>
          {matched.length > options.length && (
            <div className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
              Показаны первые {options.length} из {matched.length} — уточните поиск.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function NewContractDialog({
  existingContracts,
  onClose,
  onCreated,
}: {
  existingContracts: StudioDocumentsRegistryRow[]
  onClose: () => void
  onCreated: (contractId: number) => void
}) {
  const [action, setAction] = useState<'create' | 'renewal'>('create')
  const [contractRows, setContractRows] = useState<StudioDocumentsRegistryRow[]>(existingContracts)
  const [entities, setEntities] = useState<PartnerLegalEntity[]>([])
  const [studios, setStudios] = useState<StudioPurchaseCandidate[]>([])
  const [partners, setPartners] = useState<StudioPurchasePartner[]>([])
  const [loading, setLoading] = useState(true)
  const [studioQuery, setStudioQuery] = useState('')
  const [partnerQuery, setPartnerQuery] = useState('')
  const [studioInternalId, setStudioInternalId] = useState('')
  const [partnerNumber, setPartnerNumber] = useState('')
  const [sourceContractId, setSourceContractId] = useState('')
  const [pendingSeries, setPendingSeries] = useState<ContractPendingSeries[]>([])
  const [pendingSeriesId, setPendingSeriesId] = useState('')
  const [contractNumber, setContractNumber] = useState('')
  const [signedAt, setSignedAt] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [expectedEndDate, setExpectedEndDate] = useState('')
  const [entityId, setEntityId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      loadAllPartnerLegalEntities(),
      fetchContractPendingSeries(),
      fetchStudioPurchaseCandidates(),
      fetchStudioPurchasePartners(),
      listDocumentsRegistry(),
    ])
      .then(([legalEntities, pending, studioRows, partnerRows, registryRows]) => {
        if (cancelled) return
        setContractRows(registryRows)
        setEntities(legalEntities)
        setPendingSeries(pending)
        setStudios(studioRows)
        setPartners(partnerRows)
        setError(null)
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selectedStudio = useMemo(
    () => studios.find(row => String(row.studio_internal_id) === studioInternalId) ?? null,
    [studios, studioInternalId]
  )

  const selectedPartner = useMemo(
    () => partners.find(row => String(row.partner_number) === partnerNumber) ?? null,
    [partners, partnerNumber]
  )

  const filteredStudios = useMemo(
    () => studios
      .filter(row => optionMatches(
        [row.studio_number, row.studio_internal_id, row.city_name, row.address, row.owner_partner_number, row.owner_persons_fio],
        studioQuery
      ))
      .slice(0, 80),
    [studios, studioQuery]
  )

  const filteredPartners = useMemo(
    () => partners
      .filter(row => row.current_status?.trim().toLowerCase() !== 'бывший')
      .filter(row => optionMatches([row.partner_number, row.persons_fio, row.current_status, row.cities_text], partnerQuery))
      .slice(0, 80),
    [partners, partnerQuery]
  )

  const visiblePartnerNumber = filteredPartners.some(row => String(row.partner_number) === partnerNumber)
    ? partnerNumber
    : ''
  const visibleStudioInternalId = filteredStudios.some(row => String(row.studio_internal_id) === studioInternalId)
    ? studioInternalId
    : ''

  const matchingContracts = useMemo(() => {
    const parsedStudioId = Number(studioInternalId)
    const parsedPartnerNumber = Number(partnerNumber)
    if (!Number.isFinite(parsedStudioId) || !Number.isFinite(parsedPartnerNumber)) return []
    return contractRows.filter(row =>
      row.studio_internal_id === parsedStudioId &&
      row.partner_number === parsedPartnerNumber
    )
  }, [contractRows, partnerNumber, studioInternalId])

  const matchingPendingSeries = useMemo(() => {
    const parsedStudioId = Number(studioInternalId)
    const parsedPartnerNumber = Number(partnerNumber)
    if (!Number.isFinite(parsedStudioId) || !Number.isFinite(parsedPartnerNumber)) return []
    return pendingSeries.filter(row =>
      row.studio_internal_id === parsedStudioId &&
      row.partner_number === parsedPartnerNumber
    )
  }, [pendingSeries, partnerNumber, studioInternalId])

  const selectedSourceContract = useMemo(
    () => matchingContracts.find(row => String(row.contract_id) === sourceContractId) ?? null,
    [matchingContracts, sourceContractId]
  )

  useEffect(() => {
    if (!selectedSourceContract) return
    const dates = getContractDates(selectedSourceContract)
    const nextStart = addDaysIso(dates.actualEnd ?? dates.expectedEnd, 1)
    setEffectiveFrom(nextStart)
    setSignedAt(nextStart)
    setExpectedEndDate('')
  }, [selectedSourceContract])

  const selectedPendingSeries = useMemo(
    () => matchingPendingSeries.find(row => String(row.series_id) === pendingSeriesId) ?? matchingPendingSeries[0] ?? null,
    [matchingPendingSeries, pendingSeriesId]
  )

  const partnerEntities = useMemo(
    () => !partnerNumber
      ? []
      : entities.filter(entity => entity.partner_number === Number(partnerNumber)),
    [entities, partnerNumber]
  )

  useEffect(() => {
    if (!studioInternalId || !partnerNumber) {
      setAction('create')
      setSourceContractId('')
      setPendingSeriesId('')
      return
    }

    if (matchingContracts.length > 0) {
      setAction('renewal')
      const preferred = [...matchingContracts].sort(
        (a, b) => Number(!isActiveOrRecognized(a)) - Number(!isActiveOrRecognized(b))
      )[0]
      setSourceContractId(String(preferred.contract_id))
    } else {
      setAction('create')
      setSourceContractId('')
    }

    setPendingSeriesId(matchingPendingSeries.length === 1 ? String(matchingPendingSeries[0].series_id) : '')
    setContractNumber('')
    setSignedAt('')
    setEffectiveFrom('')
    setExpectedEndDate('')
    setEntityId('')
  }, [matchingContracts, matchingPendingSeries, partnerNumber, studioInternalId])

  const canChooseAction = studioInternalId && partnerNumber
  const isDifferentOwnerSelection = Boolean(
    selectedStudio?.owner_partner_number &&
    partnerNumber &&
    selectedStudio.owner_partner_number !== Number(partnerNumber)
  )
  const buyerName = selectedPartner
    ? (selectedPartner.persons_fio || `франчайзи №${selectedPartner.partner_number}`)
    : (partnerNumber ? `франчайзи №${partnerNumber}` : 'выбранный франчайзи')
  const sellerName = selectedStudio?.owner_persons_fio
    || (selectedStudio?.owner_partner_number ? `франчайзи №${selectedStudio.owner_partner_number}` : 'текущий владелец')
  const saleConfirmationText = selectedStudio && isDifferentOwnerSelection
    ? `Вы подтверждаете, что ${buyerName} собирается купить студию ${selectedStudio.studio_number ?? selectedStudio.studio_internal_id} у ${sellerName}?`
    : ''
  const canSubmit = Boolean(
    canChooseAction &&
    contractNumber.trim() &&
    effectiveFrom &&
    (action === 'renewal'
      ? sourceContractId
      : (matchingPendingSeries.length <= 1 || selectedPendingSeries))
  )

  async function handleSubmit() {
    const dateValues = [signedAt, effectiveFrom, expectedEndDate]
    if (!dateValues.every(isValidIsoDate)) {
      setError('Введите даты в формате дд.мм.гггг.')
      return
    }

    if (saleConfirmationText && !window.confirm(saleConfirmationText)) {
      return
    }

    setSaving(true)
    setError(null)
    try {
      let newContractId: number
      if (action === 'renewal') {
        const parsedSourceContractId = Number(sourceContractId)
        if (!Number.isFinite(parsedSourceContractId) || !contractNumber.trim() || !effectiveFrom) return
        newContractId = await createContractRenewalFromContract({
          sourceContractId: parsedSourceContractId,
          contractNumber,
          signedAt: signedAt || effectiveFrom,
          effectiveFrom,
          expectedEndDate: expectedEndDate || null,
          closeSource: true,
        })
      } else {
        const parsedPartnerNumber = Number(partnerNumber)
        const parsedStudioId = Number(studioInternalId)
        if (!Number.isFinite(parsedPartnerNumber) || !Number.isFinite(parsedStudioId) || !contractNumber.trim() || !effectiveFrom) return
        let seriesId = selectedPendingSeries?.series_id ?? null

        if (seriesId == null) {
          const planned = await planStudioPurchase({
            studioInternalId: parsedStudioId,
            buyerPartnerNumber: parsedPartnerNumber,
            note: 'Автосоздание серии и владения при добавлении ДКК из реестра документов',
          })
          seriesId = planned.series_id
        }

        newContractId = await createContractForSeries({
          seriesId,
          contractNumber,
          signedAt: signedAt || effectiveFrom,
          effectiveFrom,
          expectedEndDate: expectedEndDate || null,
          entityId: entityId ? Number(entityId) : null,
        })
      }
      onCreated(newContractId)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg border border-border bg-background p-4 shadow-xl">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-base font-semibold">Добавить ДКК</div>
            <div className="text-xs text-muted-foreground">
              Выберите франчайзи и студию. Если серии ещё нет, система создаст серию и владение; если договор уже есть, предложит продление.
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
            <X size={16} />
          </button>
        </div>

        {error && (
          <RegistryErrorAlert className="mb-3">{error}</RegistryErrorAlert>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground">Загрузка...</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground">Франчайзи</label>
                <div className="relative mt-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={partnerQuery}
                    onChange={e => setPartnerQuery(e.target.value)}
                    placeholder="номер, ФИО, город"
                    className="w-full pl-8 pr-3 py-2 border border-border rounded-md bg-background text-sm"
                  />
                </div>
                <select
                  value={visiblePartnerNumber}
                  onChange={e => setPartnerNumber(e.target.value)}
                  size={8}
                  className="mt-1 w-full px-3 py-1.5 border border-border rounded-md bg-background text-sm"
                >
                  <option value="">— выберите франчайзи из списка —</option>
                  {filteredPartners.map(row => (
                    <option key={row.partner_number} value={String(row.partner_number)}>
                      {partnerOptionLabel(row)}
                      {row.current_status ? ` · ${row.current_status}` : ''}
                      {row.active_studio_count ? ` · студий: ${row.active_studio_count}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Студия</label>
                <div className="relative mt-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={studioQuery}
                    onChange={e => setStudioQuery(e.target.value)}
                    placeholder="номер, город, адрес, владелец"
                    className="w-full pl-8 pr-3 py-2 border border-border rounded-md bg-background text-sm"
                  />
                </div>
                <select
                  value={visibleStudioInternalId}
                  onChange={e => setStudioInternalId(e.target.value)}
                  size={8}
                  className="mt-1 w-full px-3 py-1.5 border border-border rounded-md bg-background text-sm"
                >
                  <option value="">— выберите студию из списка —</option>
                  {filteredStudios.map(row => (
                    <option key={row.studio_internal_id} value={String(row.studio_internal_id)}>
                      {studioOptionLabel(row)}
                      {row.owner_partner_number ? ` · №${row.owner_partner_number} ${row.owner_persons_fio}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {isDifferentOwnerSelection && (
              <div className="rounded-md border-2 border-red-500 bg-red-50 px-3 py-2 text-sm text-red-800">
                <div className="font-semibold">Внимание: выбрана чужая студия</div>
                <div>
                  {saleConfirmationText} Это создаст серию и владение для покупателя, а не для текущего владельца.
                </div>
              </div>
            )}

            {canChooseAction && (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                <div className="font-medium text-foreground">
                  {selectedPartner ? partnerOptionLabel(selectedPartner) : `№${partnerNumber}`} · {selectedStudio ? studioOptionLabel(selectedStudio) : `студия ${studioInternalId}`}
                </div>
                {matchingContracts.length > 0
                  ? `Найдено договоров: ${matchingContracts.length}. По умолчанию будет создано продление.`
                  : selectedPendingSeries
                    ? `ДКК будет создан в серии ${selectedPendingSeries.series_id}, владение ${selectedPendingSeries.ownership_id}.`
                    : 'Серии без ДКК не найдено: при сохранении система создаст серию и владение BUYING, затем добавит ДКК.'}
              </div>
            )}

            {canChooseAction && matchingContracts.length > 0 && (
              <div className="inline-flex rounded-md border border-border bg-muted/30 p-1 text-sm">
                <button
                  type="button"
                  onClick={() => setAction('renewal')}
                  className={`rounded px-3 py-1.5 ${action === 'renewal' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Продлить существующий
                </button>
              </div>
            )}

            {canChooseAction && action === 'create' && matchingPendingSeries.length > 1 && (
              <div>
                <label className="text-xs text-muted-foreground">Уточните серию</label>
                <select
                  value={pendingSeriesId}
                  onChange={e => setPendingSeriesId(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                >
                  <option value="">— выберите —</option>
                  {matchingPendingSeries.map(row => (
                    <option key={row.series_id} value={String(row.series_id)}>
                      Серия {row.series_id} · владение {row.ownership_id} · {OWNERSHIP_PHASE_LABELS[row.ownership_phase] ?? row.ownership_phase}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {canChooseAction && action === 'renewal' ? (
              <>
                <div>
                  <label className="text-xs text-muted-foreground">Какой ДКК продлеваем</label>
                  <ContractSearchSelect
                    contracts={matchingContracts}
                    value={sourceContractId}
                    onChange={setSourceContractId}
                  />
                </div>

                {selectedSourceContract && (() => {
                  const dates = getContractDates(selectedSourceContract)
                  return (
                    <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                      Текущий срок: {fmtDate(dates.effectiveFrom)} — {fmtDate(dates.actualEnd ?? dates.expectedEnd)}.
                      Старый ДКК и последний интервал условий будут закрыты днём перед стартом нового.
                    </div>
                  )
                })()}
              </>
            ) : canChooseAction ? (
              <>
                <div>
                  <label className="text-xs text-muted-foreground">Юрлицо франчайзи</label>
                  <select
                    value={entityId}
                    onChange={e => setEntityId(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm disabled:opacity-60"
                  >
                    <option value="">— не выбрано —</option>
                    {partnerEntities.map(entity => (
                      <option key={entity.entity_id} value={String(entity.entity_id)}>
                        {entity.legal_name} · ИНН {entity.inn ?? '—'}
                      </option>
                    ))}
                  </select>
                  {partnerNumber && (
                    <AddPartnerEntityInline
                      partnerNumber={Number(partnerNumber)}
                      onCreated={entity => {
                        setEntities(list => [...list, entity])
                        setEntityId(String(entity.entity_id))
                      }}
                    />
                  )}
                  {partnerNumber && partnerEntities.length === 0 && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      У франчайзи №{partnerNumber} юрлиц ещё нет — заведите здесь же.
                    </div>
                  )}
                </div>
              </>
            ) : null}

            {canChooseAction && (
              <>
                <div>
                  <label className="text-xs text-muted-foreground">
                    {action === 'renewal' ? 'Номер нового ДКК' : 'Номер ДКК'}
                  </label>
                  <input
                    type="text"
                    value={contractNumber}
                    onChange={e => setContractNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    placeholder={action === 'renewal' ? 'Например: 618-Н/2' : undefined}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Подписан</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="дд.мм.гггг"
                      value={dateInputDisplay(signedAt)}
                      onChange={e => setSignedAt(parseDateInputDisplay(e.target.value))}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Начало действия</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="дд.мм.гггг"
                      value={dateInputDisplay(effectiveFrom)}
                      onChange={e => {
                        const next = parseDateInputDisplay(e.target.value)
                        setEffectiveFrom(next)
                        setSignedAt(signedAt || next)
                      }}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Документальный срок</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="дд.мм.гггг"
                      value={dateInputDisplay(expectedEndDate)}
                      onChange={e => setExpectedEndDate(parseDateInputDisplay(e.target.value))}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Отмена
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={
              saving ||
              loading ||
              !canSubmit
            }
          >
            {saving && <RefreshCw size={14} className="mr-1 animate-spin" />}
            {action === 'renewal' ? 'Создать продление' : 'Создать ДКК'}
          </Button>
        </div>
      </div>
    </div>
  )
}
